import type { AssetHistoryEntry, LngLat, RouteCategory, RouteDetail, RouteMode } from '@ftthnms/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export const useRoute = (id: string | null | undefined) =>
  useQuery({
    queryKey: ['route', id],
    queryFn: () => api<RouteDetail>(`/api/v1/routes/${id}`),
    enabled: !!id,
  });

export const useRouteHistory = (id: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: ['route', id, 'history'],
    queryFn: () => api<AssetHistoryEntry[]>(`/api/v1/routes/${id}/history`),
    enabled: !!id && enabled,
  });

export interface CableType {
  code: string;
  name: string;
  attenuationDbPerKm: number;
  pricePerM: number | null;
}

export const useCableTypes = () =>
  useQuery({
    queryKey: ['master', 'cable-types'],
    queryFn: () => api<CableType[]>('/api/v1/master/cable-types'),
    staleTime: 10 * 60_000,
  });

/** A route drawn on the map and not saved yet. */
export interface RouteDraft {
  from: { id: string; code: string; typeCode: string };
  to: { id: string; code: string; typeCode: string };
  mode: RouteMode;
  path: LngLat[];
  lengthM: number;
  warning: string | null;
}

/** Sensible defaults from the two ends: drop to customers, feeder out of an OLT. */
export function defaultsFor(
  fromType: string,
  toType: string,
): { category: RouteCategory; cableType: string; coreCount: number } {
  if (toType === 'CPE') return { category: 'drop', cableType: 'DROP', coreCount: 1 };
  if (fromType === 'OLT' || fromType === 'SERVER')
    return { category: 'feeder', cableType: 'ADSS', coreCount: 24 };
  return { category: 'distribution', cableType: 'ADSS', coreCount: 12 };
}
